import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBolt,
  FaCoins,
  FaHandPaper,
  FaLayerGroup,
  FaLightbulb,
  FaMoneyBillWave,
  FaPlay,
  FaRedoAlt,
  FaShieldAlt,
  FaUniversity,
} from "react-icons/fa";
import "./Blackjack.css";
import GameRoomBackButton from "../GameRoomBackButton";
import {
  claimBlackjackRefill,
  getBlackjackWallet,
  syncSoloBlackjackWallet,
} from "../../../services/games/gameService";

const SUITS = ["♠", "♥", "♦", "♣"];
const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

const SHOE_DECKS = 6;
const MAX_SPLIT_HANDS = 4;
const DEALER_HITS_SOFT_17 = false;

const STARTING_BANKROLL = 1000;
const MIN_BET = 25;
const BET_STEP = 25;

function buildShoe() {
  const cards = [];
  let id = 0;

  for (let deck = 0; deck < SHOE_DECKS; deck += 1) {
    for (const suit of SUITS) {
      for (const rank of RANKS) {
        cards.push({ rank, suit, id: `${deck}-${id++}` });
      }
    }
  }

  for (let i = cards.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }

  return cards;
}

function rankValue(rank) {
  if (rank === "A") return 11;
  if (["K", "Q", "J"].includes(rank)) return 10;
  return Number(rank);
}

function handValue(cards) {
  let total = cards.reduce((sum, card) => sum + rankValue(card.rank), 0);
  let aces = cards.filter((card) => card.rank === "A").length;

  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }

  const rawTotal = cards.reduce(
    (sum, card) => sum + rankValue(card.rank),
    0
  );

  return {
    total,
    soft:
      cards.some((card) => card.rank === "A") &&
      rawTotal === total,
  };
}

function isNaturalBlackjack(hand) {
  return (
    !hand.fromSplit &&
    hand.cards.length === 2 &&
    handValue(hand.cards).total === 21
  );
}

function canSplitCards(cards) {
  return (
    cards.length === 2 &&
    rankValue(cards[0].rank) === rankValue(cards[1].rank)
  );
}

function makeHand(cards, bet, extra = {}) {
  return {
    cards,
    bet,
    stood: false,
    busted: false,
    surrendered: false,
    doubled: false,
    fromSplit: false,
    result: "",
    payout: 0,
    ...extra,
  };
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString()}`;
}

function formatRefillCountdown(iso) {
  if (!iso) return null;
  const ms = Math.max(0, new Date(iso).getTime() - Date.now());
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function dealerUpValue(card) {
  if (!card) return null;
  if (card.rank === "A") return 11;
  return rankValue(card.rank);
}

/**
 * Multi-deck, dealer-stands-soft-17 basic strategy guidance.
 * This is intentionally advisory only; the player can choose any legal action.
 */
function getBasicStrategyRecommendation(hand, dealerCard, canDouble, canSplit, canSurrender) {
  if (!hand || !dealerCard) return null;

  const dealer = dealerUpValue(dealerCard);
  const { total, soft } = handValue(hand.cards);
  const pair = hand.cards.length === 2 && canSplitCards(hand.cards);
  const pairValue = pair ? rankValue(hand.cards[0].rank) : null;

  // Pairs first.
  if (pair && canSplit) {
    if (pairValue === 11 || pairValue === 8) {
      return { action: "split", label: "Split", reason: "Basic strategy splits aces and eights." };
    }

    if (pairValue === 10) {
      return { action: "stand", label: "Stand", reason: "Twenty is already a very strong hand." };
    }

    if (pairValue === 9) {
      if ([2, 3, 4, 5, 6, 8, 9].includes(dealer)) {
        return { action: "split", label: "Split", reason: "Pair strategy favors splitting nines here." };
      }
      return { action: "stand", label: "Stand", reason: "Keep 18 against this dealer up-card." };
    }

    if (pairValue === 7) {
      if ([2, 3, 4, 5, 6, 7].includes(dealer)) {
        return { action: "split", label: "Split", reason: "Pair strategy favors splitting sevens here." };
      }
      return { action: "hit", label: "Hit", reason: "Dealer strength makes hitting preferable." };
    }

    if (pairValue === 6) {
      if ([2, 3, 4, 5, 6].includes(dealer)) {
        return { action: "split", label: "Split", reason: "With double-after-split available, split sixes here." };
      }
      return { action: "hit", label: "Hit", reason: "Dealer strength makes hitting preferable." };
    }

    if (pairValue === 4) {
      if ([5, 6].includes(dealer)) {
        return { action: "split", label: "Split", reason: "Split fours against dealer 5 or 6." };
      }
      return { action: "hit", label: "Hit", reason: "Otherwise treat the pair as a hard 8." };
    }

    if ([2, 3].includes(pairValue)) {
      if ([2, 3, 4, 5, 6, 7].includes(dealer)) {
        return { action: "split", label: "Split", reason: "Pair strategy favors splitting small pairs here." };
      }
      return { action: "hit", label: "Hit", reason: "Dealer strength makes hitting preferable." };
    }
    // Pair fives intentionally fall through to hard 10 strategy.
  }

  // Late surrender before ordinary hard-total strategy.
  if (!soft && canSurrender) {
    if (total === 16 && [9, 10, 11].includes(dealer)) {
      return { action: "surrender", label: "Surrender", reason: "Basic strategy surrenders hard 16 against a strong dealer card." };
    }

    if (total === 15 && dealer === 10) {
      return { action: "surrender", label: "Surrender", reason: "Basic strategy surrenders hard 15 against dealer 10." };
    }
  }

  // Soft totals.
  if (soft) {
    if (total >= 20) {
      return { action: "stand", label: "Stand", reason: "Soft 20 or better is strong enough to stand." };
    }

    if (total === 19) {
      if (dealer === 6 && canDouble) {
        return { action: "double", label: "Double", reason: "Soft 19 can double against dealer 6 under these rules." };
      }
      return { action: "stand", label: "Stand", reason: "Soft 19 is normally a stand." };
    }

    if (total === 18) {
      if ([3, 4, 5, 6].includes(dealer) && canDouble) {
        return { action: "double", label: "Double", reason: "Soft 18 is a strong double against this dealer card." };
      }
      if ([2, 7, 8].includes(dealer)) {
        return { action: "stand", label: "Stand", reason: "Soft 18 stands against this dealer card." };
      }
      return { action: "hit", label: "Hit", reason: "Soft 18 should improve against a strong dealer card." };
    }

    if (total === 17) {
      if ([3, 4, 5, 6].includes(dealer) && canDouble) {
        return { action: "double", label: "Double", reason: "Soft 17 is a profitable double against this dealer card." };
      }
      return { action: "hit", label: "Hit", reason: "Soft 17 is usually improved by taking another card." };
    }

    if ([15, 16].includes(total)) {
      if ([4, 5, 6].includes(dealer) && canDouble) {
        return { action: "double", label: "Double", reason: "This soft total doubles against a weak dealer card." };
      }
      return { action: "hit", label: "Hit", reason: "Keep improving this soft hand." };
    }

    if ([13, 14].includes(total)) {
      if ([5, 6].includes(dealer) && canDouble) {
        return { action: "double", label: "Double", reason: "This soft total doubles against dealer 5 or 6." };
      }
      return { action: "hit", label: "Hit", reason: "Keep improving this soft hand." };
    }
  }

  // Hard totals.
  if (total >= 17) {
    return { action: "stand", label: "Stand", reason: "Basic strategy stands on hard 17 or more." };
  }

  if (total >= 13 && total <= 16) {
    if ([2, 3, 4, 5, 6].includes(dealer)) {
      return { action: "stand", label: "Stand", reason: "Let the dealer's weak up-card do the work." };
    }
    return { action: "hit", label: "Hit", reason: "A strong dealer up-card calls for improving your hand." };
  }

  if (total === 12) {
    if ([4, 5, 6].includes(dealer)) {
      return { action: "stand", label: "Stand", reason: "Hard 12 stands against dealer 4 through 6." };
    }
    return { action: "hit", label: "Hit", reason: "Hard 12 usually needs improvement here." };
  }

  if (total === 11) {
    if (dealer !== 11 && canDouble) {
      return { action: "double", label: "Double", reason: "Hard 11 is one of the strongest doubling hands." };
    }
    return { action: "hit", label: "Hit", reason: "Against an ace, take another card rather than double." };
  }

  if (total === 10) {
    if (dealer >= 2 && dealer <= 9 && canDouble) {
      return { action: "double", label: "Double", reason: "Hard 10 doubles against dealer 2 through 9." };
    }
    return { action: "hit", label: "Hit", reason: "Dealer 10 or ace is too strong for the double." };
  }

  if (total === 9) {
    if ([3, 4, 5, 6].includes(dealer) && canDouble) {
      return { action: "double", label: "Double", reason: "Hard 9 doubles against dealer 3 through 6." };
    }
    return { action: "hit", label: "Hit", reason: "Otherwise, improve the hand with a hit." };
  }

  return { action: "hit", label: "Hit", reason: "Basic strategy hits hard 8 or less." };
}

export default function GameRoomBlackjack({ currentUser }) {

  const [shoe, setShoe] = useState(() => buildShoe());
  const [dealerHand, setDealerHand] = useState([]);
  const [playerHands, setPlayerHands] = useState([]);
  const [currentHand, setCurrentHand] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("Choose a wager and deal a hand.");

  const [bankroll, setBankroll] = useState(STARTING_BANKROLL);
  const [wager, setWager] = useState(MIN_BET);
  const [walletMeta, setWalletMeta] = useState(null);
  const [walletLoaded, setWalletLoaded] = useState(!currentUser?.id);
  const refillInFlightRef = useRef(false);
  const walletSyncTimerRef = useRef(null);

  const [insuranceOffer, setInsuranceOffer] = useState(null);
  const [insuranceBet, setInsuranceBet] = useState(0);
  const [showRecommendations, setShowRecommendations] = useState(true);

  const [stats, setStats] = useState({
    wins: 0,
    losses: 0,
    pushes: 0,
    blackjacks: 0,
    hands: 0,
  });

  useEffect(() => {
    let cancelled = false;

    const loadWallet = async () => {
      if (!currentUser?.id) {
        setWalletLoaded(true);
        return;
      }

      try {
        const wallet = await getBlackjackWallet();
        if (cancelled || !wallet) return;
        const balance = Number(wallet.balance || 0);
        setWalletMeta(wallet);
        setBankroll(balance);
        setWager((prev) =>
          balance >= MIN_BET
            ? Math.max(MIN_BET, Math.min(prev, Math.floor(balance / BET_STEP) * BET_STEP))
            : MIN_BET
        );
      } catch (err) {
        console.error("Blackjack wallet load failed:", err);
      } finally {
        if (!cancelled) setWalletLoaded(true);
      }
    };

    loadWallet();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id || !walletLoaded) return undefined;

    if (walletSyncTimerRef.current) {
      window.clearTimeout(walletSyncTimerRef.current);
    }

    walletSyncTimerRef.current = window.setTimeout(async () => {
      try {
        await syncSoloBlackjackWallet(bankroll);
        setWalletMeta((prev) => prev ? { ...prev, balance: bankroll } : prev);
      } catch (err) {
        console.warn("Blackjack wallet sync failed:", err);
      }
    }, 250);

    return () => {
      if (walletSyncTimerRef.current) {
        window.clearTimeout(walletSyncTimerRef.current);
      }
    };
  }, [bankroll, currentUser?.id, walletLoaded]);

  useEffect(() => {
    if (!currentUser?.id || !walletLoaded || bankroll >= MIN_BET) return;
    if (Number(walletMeta?.refills_remaining ?? 2) <= 0) return;
    refillBankroll();
  }, [bankroll, currentUser?.id, walletLoaded, walletMeta?.refills_remaining]);

  const activeHand = playerHands[currentHand];

  const visibleDealerTotal = useMemo(() => {
    if (!dealerHand.length) return null;

    if (phase === "player" || phase === "insurance") {
      return handValue([dealerHand[0]]).total;
    }

    return handValue(dealerHand).total;
  }, [dealerHand, phase]);

  const drawFromShoe = (workingShoe) => {
    if (workingShoe.length < 52) {
      const replacement = buildShoe();
      workingShoe.splice(0, workingShoe.length, ...replacement);
    }

    return workingShoe.pop();
  };

  const changeWager = (amount) => {
    if (phase !== "idle" && phase !== "complete") return;

    setWager((prev) => {
      const maxBet = Math.max(MIN_BET, bankroll);
      return Math.max(MIN_BET, Math.min(prev + amount, maxBet));
    });
  };

  const refillBankroll = async () => {
    if (bankroll >= MIN_BET || refillInFlightRef.current) return;

    if (!currentUser?.id) {
      setBankroll(STARTING_BANKROLL);
      setMessage(`Play chips refreshed to ${formatMoney(STARTING_BANKROLL)}.`);
      return;
    }

    refillInFlightRef.current = true;
    try {
      const wallet = await claimBlackjackRefill();
      setWalletMeta(wallet);
      setBankroll(Number(wallet?.balance || 0));
      setWager((prev) => Math.max(MIN_BET, Math.min(prev, Number(wallet?.balance || 0))));
      setMessage(
        `Play chips refreshed to ${formatMoney(wallet?.balance)}. ${wallet?.refills_remaining ?? 0} free refresh${Number(wallet?.refills_remaining) === 1 ? "" : "es"} remaining.`
      );
    } catch (err) {
      if (String(err?.message || "").includes("BLACKJACK_REFILLS_EXHAUSTED")) {
        const wallet = await getBlackjackWallet().catch(() => null);
        if (wallet) setWalletMeta(wallet);
        setMessage("Free chip refreshes are used for this 24-hour window.");
      } else {
        setMessage(err?.message || "Could not refresh play chips.");
      }
    } finally {
      refillInFlightRef.current = false;
    }
  };

  const resolveOpeningBlackjack = (firstHand, dealer, roundBet, insuranceWager = 0) => {
    const playerBlackjack = isNaturalBlackjack(firstHand);
    const dealerBlackjack = handValue(dealer).total === 21;

    if (!playerBlackjack && !dealerBlackjack) return false;

    let result = "";
    let mainPayout = 0;
    let insurancePayout = 0;
    let statPatch = {};

    if (insuranceWager > 0 && dealerBlackjack) {
      // 2:1 insurance profit plus return of the insurance stake.
      insurancePayout = insuranceWager * 3;
    }

    if (playerBlackjack && dealerBlackjack) {
      result = insuranceWager > 0
        ? "Both have blackjack. Main hand pushes; insurance wins 2:1."
        : "Push — both have blackjack.";
      mainPayout = roundBet;
      statPatch = { pushes: 1 };
    } else if (playerBlackjack) {
      result = `Blackjack! ${formatMoney(roundBet * 1.5)} profit.`;
      mainPayout = roundBet * 2.5;
      statPatch = { wins: 1, blackjacks: 1 };
    } else {
      result = insuranceWager > 0
        ? "Dealer blackjack. Insurance pays 2:1."
        : "Dealer blackjack.";
      statPatch = { losses: 1 };
    }

    setPlayerHands([
      {
        ...firstHand,
        stood: true,
        result,
        payout: mainPayout,
      },
    ]);

    setBankroll((prev) => prev + mainPayout + insurancePayout);

    setStats((prev) => ({
      ...prev,
      wins: prev.wins + (statPatch.wins || 0),
      losses: prev.losses + (statPatch.losses || 0),
      pushes: prev.pushes + (statPatch.pushes || 0),
      blackjacks: prev.blackjacks + (statPatch.blackjacks || 0),
      hands: prev.hands + 1,
    }));

    setPhase("complete");
    setInsuranceOffer(null);
    setInsuranceBet(0);
    setMessage(result);
    return true;
  };

  const startRound = () => {
    if (bankroll < MIN_BET) {
      setMessage("You're out of chips. Use the Hobbli ATM to keep playing.");
      return;
    }

    const roundBet = Math.min(
      Math.max(MIN_BET, wager),
      bankroll
    );

    const workingShoe = [...shoe];
    const p1 = drawFromShoe(workingShoe);
    const d1 = drawFromShoe(workingShoe);
    const p2 = drawFromShoe(workingShoe);
    const d2 = drawFromShoe(workingShoe);

    const firstHand = makeHand([p1, p2], roundBet);
    const dealer = [d1, d2];

    setShoe(workingShoe);
    setBankroll((prev) => prev - roundBet);
    setDealerHand(dealer);
    setPlayerHands([firstHand]);
    setCurrentHand(0);
    setInsuranceBet(0);

    const dealerShowsAce = d1.rank === "A";
    const dealerShowsTenValue = rankValue(d1.rank) === 10;

    if (dealerShowsAce) {
      const maxInsurance = Math.min(roundBet / 2, bankroll - roundBet);
      setInsuranceOffer({
        max: Math.max(0, maxInsurance),
        roundBet,
        firstHand,
        dealer,
      });
      setPhase("insurance");
      setMessage("Dealer shows an ace. Insurance is available before play continues.");
      return;
    }

    if (dealerShowsTenValue) {
      if (resolveOpeningBlackjack(firstHand, dealer, roundBet, 0)) {
        return;
      }
    } else if (isNaturalBlackjack(firstHand)) {
      resolveOpeningBlackjack(firstHand, dealer, roundBet, 0);
      return;
    }

    setPhase("player");
    setMessage(`Your move — ${formatMoney(roundBet)} on Hand 1.`);
  };

  const handleInsuranceDecision = (takeInsurance) => {
    if (phase !== "insurance" || !insuranceOffer) return;

    const { max, roundBet, firstHand, dealer } = insuranceOffer;
    const amount = takeInsurance ? max : 0;

    if (amount > 0) {
      setBankroll((prev) => prev - amount);
      setInsuranceBet(amount);
    }

    const dealerBlackjack = handValue(dealer).total === 21;
    const playerBlackjack = isNaturalBlackjack(firstHand);

    if (dealerBlackjack || playerBlackjack) {
      resolveOpeningBlackjack(firstHand, dealer, roundBet, amount);
      return;
    }

    // Dealer does not have blackjack, so any insurance stake is lost.
    setInsuranceOffer(null);
    setPhase("player");

    if (amount > 0) {
      setMessage(
        `Dealer does not have blackjack. Insurance loses ${formatMoney(amount)}. Your move.`
      );
    } else {
      setMessage("No dealer blackjack. Your move.");
    }
  };

  const settleRound = (finalHands, finalDealer) => {
    const dealerTotal = handValue(finalDealer).total;
    const dealerBust = dealerTotal > 21;

    const outcomes = {
      wins: 0,
      losses: 0,
      pushes: 0,
      hands: finalHands.length,
    };

    let totalReturn = 0;

    const settled = finalHands.map((hand) => {
      const total = handValue(hand.cards).total;
      let result = "";
      let payout = 0;

      if (hand.surrendered) {
        payout = hand.bet / 2;
        result = `Surrender — ${formatMoney(hand.bet / 2)} returned.`;
        outcomes.losses += 1;
      } else if (hand.busted || total > 21) {
        result = `Bust — lose ${formatMoney(hand.bet)}.`;
        outcomes.losses += 1;
      } else if (dealerBust) {
        payout = hand.bet * 2;
        result = `Dealer busts — win ${formatMoney(hand.bet)}.`;
        outcomes.wins += 1;
      } else if (total > dealerTotal) {
        payout = hand.bet * 2;
        result = `You win ${formatMoney(hand.bet)}.`;
        outcomes.wins += 1;
      } else if (total < dealerTotal) {
        result = `Dealer wins — lose ${formatMoney(hand.bet)}.`;
        outcomes.losses += 1;
      } else {
        payout = hand.bet;
        result = `Push — ${formatMoney(hand.bet)} returned.`;
        outcomes.pushes += 1;
      }

      totalReturn += payout;

      return {
        ...hand,
        stood: true,
        result,
        payout,
      };
    });

    setPlayerHands(settled);
    setDealerHand(finalDealer);
    setBankroll((prev) => prev + totalReturn);

    setStats((prev) => ({
      ...prev,
      wins: prev.wins + outcomes.wins,
      losses: prev.losses + outcomes.losses,
      pushes: prev.pushes + outcomes.pushes,
      hands: prev.hands + outcomes.hands,
    }));

    setPhase("complete");
    setMessage(
      dealerBust
        ? "Dealer busts. Round complete."
        : `Dealer stands on ${dealerTotal}.`
    );
  };

  const playDealer = (finalHands) => {
    const liveHands = finalHands.filter(
      (hand) =>
        !hand.busted &&
        !hand.surrendered &&
        handValue(hand.cards).total <= 21
    );

    if (!liveHands.length) {
      settleRound(finalHands, dealerHand);
      return;
    }

    const workingShoe = [...shoe];
    const dealer = [...dealerHand];

    while (true) {
      const { total, soft } = handValue(dealer);

      const shouldHit =
        total < 17 ||
        (total === 17 && soft && DEALER_HITS_SOFT_17);

      if (!shouldHit) break;

      dealer.push(drawFromShoe(workingShoe));
    }

    setShoe(workingShoe);
    setDealerHand(dealer);
    settleRound(finalHands, dealer);
  };

  const advanceAfterAction = (hands, finishedIndex = currentHand) => {
    let nextIndex = finishedIndex + 1;

    while (nextIndex < hands.length && hands[nextIndex].stood) {
      nextIndex += 1;
    }

    if (nextIndex < hands.length) {
      setPlayerHands(hands);
      setCurrentHand(nextIndex);
      setMessage(
        `Hand ${nextIndex + 1} is up — ${formatMoney(
          hands[nextIndex].bet
        )} wagered.`
      );
      return;
    }

    setPlayerHands(hands);
    setPhase("dealer");
    setMessage("Dealer is playing...");

    window.setTimeout(() => {
      playDealer(hands);
    }, 350);
  };

  const hit = () => {
    if (phase !== "player" || !activeHand) return;

    const workingShoe = [...shoe];
    const hands = playerHands.map((hand) => ({
      ...hand,
      cards: [...hand.cards],
    }));

    hands[currentHand].cards.push(
      drawFromShoe(workingShoe)
    );

    const total = handValue(
      hands[currentHand].cards
    ).total;

    // Only a bust automatically ends the player's hand.
    // Even 21 remains playable if the player wants to hit again.
    if (total > 21) {
      hands[currentHand].busted = true;
      hands[currentHand].stood = true;
    }

    setShoe(workingShoe);

    if (hands[currentHand].stood) {
      advanceAfterAction(hands, currentHand);
    } else {
      setPlayerHands(hands);

      if (total === 21) {
        setMessage(
          `Hand ${currentHand + 1}: 21. You can stand or hit again.`
        );
      } else {
        setMessage(`Hand ${currentHand + 1}: ${total}.`);
      }
    }
  };

  const stand = () => {
    if (phase !== "player" || !activeHand) return;

    const hands = playerHands.map((hand) => ({
      ...hand,
    }));

    hands[currentHand].stood = true;
    advanceAfterAction(hands, currentHand);
  };

  const doubleDown = () => {
    if (
      phase !== "player" ||
      !activeHand ||
      activeHand.cards.length !== 2 ||
      bankroll < activeHand.bet
    ) {
      return;
    }

    const workingShoe = [...shoe];
    const hands = playerHands.map((hand) => ({
      ...hand,
      cards: [...hand.cards],
    }));

    const extraBet = hands[currentHand].bet;

    hands[currentHand].bet += extraBet;
    hands[currentHand].cards.push(
      drawFromShoe(workingShoe)
    );
    hands[currentHand].doubled = true;
    hands[currentHand].stood = true;
    hands[currentHand].busted =
      handValue(hands[currentHand].cards).total > 21;

    setBankroll((prev) => prev - extraBet);
    setShoe(workingShoe);

    advanceAfterAction(hands, currentHand);
  };

  const surrender = () => {
    if (
      phase !== "player" ||
      !activeHand ||
      activeHand.cards.length !== 2 ||
      activeHand.fromSplit
    ) {
      return;
    }

    const hands = playerHands.map((hand) => ({
      ...hand,
    }));

    hands[currentHand].surrendered = true;
    hands[currentHand].stood = true;

    advanceAfterAction(hands, currentHand);
  };

  const split = () => {
    if (
      phase !== "player" ||
      !activeHand ||
      playerHands.length >= MAX_SPLIT_HANDS ||
      !canSplitCards(activeHand.cards) ||
      bankroll < activeHand.bet
    ) {
      return;
    }

    const workingShoe = [...shoe];
    const [leftCard, rightCard] = activeHand.cards;
    const splitBet = activeHand.bet;

    const leftHand = makeHand(
      [leftCard, drawFromShoe(workingShoe)],
      splitBet,
      { fromSplit: true }
    );

    const rightHand = makeHand(
      [rightCard, drawFromShoe(workingShoe)],
      splitBet,
      { fromSplit: true }
    );

    const hands = [
      ...playerHands.slice(0, currentHand),
      leftHand,
      rightHand,
      ...playerHands.slice(currentHand + 1),
    ];

    setBankroll((prev) => prev - splitBet);
    setShoe(workingShoe);
    setPlayerHands(hands);
    setCurrentHand(currentHand);

    setMessage(
      `Split complete. Play Hand ${currentHand + 1}, then Hand ${
        currentHand + 2
      }.`
    );
  };

  const isRed = (suit) =>
    suit === "♥" || suit === "♦";

  const renderCard = (card, hidden = false) => (
    <div
      className={`bj-card ${
        hidden ? "bj-card--hidden" : ""
      }`}
    >
      {hidden ? (
        <div className="bj-card__back" />
      ) : (
        <>
          <div
            className={`bj-card__corner ${
              isRed(card.suit) ? "is-red" : ""
            }`}
          >
            <strong>{card.rank}</strong>
            <span>{card.suit}</span>
          </div>

          <div
            className={`bj-card__suit ${
              isRed(card.suit) ? "is-red" : ""
            }`}
          >
            {card.suit}
          </div>

          <div
            className={`bj-card__corner bj-card__corner--bottom ${
              isRed(card.suit) ? "is-red" : ""
            }`}
          >
            <strong>{card.rank}</strong>
            <span>{card.suit}</span>
          </div>
        </>
      )}
    </div>
  );

  const canDouble =
    phase === "player" &&
    activeHand?.cards.length === 2 &&
    bankroll >= (activeHand?.bet || 0);

  const canSurrender =
    phase === "player" &&
    activeHand?.cards.length === 2 &&
    !activeHand?.fromSplit;

  const canSplit =
    phase === "player" &&
    activeHand &&
    playerHands.length < MAX_SPLIT_HANDS &&
    canSplitCards(activeHand.cards) &&
    bankroll >= activeHand.bet;

  const recommendation = useMemo(() => {
    if (!showRecommendations || phase !== "player" || !activeHand || !dealerHand[0]) {
      return null;
    }

    return getBasicStrategyRecommendation(
      activeHand,
      dealerHand[0],
      canDouble,
      canSplit,
      canSurrender
    );
  }, [
    phase,
    activeHand,
    dealerHand,
    canDouble,
    canSplit,
    canSurrender,
    showRecommendations,
  ]);

  const roundWagered = playerHands.reduce(
    (sum, hand) => sum + hand.bet,
    0
  );

  const displayWager =
    phase === "player" || phase === "dealer" || phase === "insurance"
      ? roundWagered + insuranceBet
      : wager;

  const actionClass = (action, base = "bj-action") =>
    `${base} ${
      recommendation?.action === action
        ? "is-recommended"
        : ""
    }`;

  const atmAvailable = bankroll < MIN_BET;

  const splitFundingShortfall = activeHand
    ? Math.max(0, activeHand.bet - bankroll)
    : 0;

  const doubleFundingShortfall = activeHand
    ? Math.max(0, activeHand.bet - bankroll)
    : 0;

  return (
    <div className="hobbli-blackjack">
      <header className="bj-header">
        <GameRoomBackButton />

        <div>
          <span>Hobbli Game Room</span>
          <h1>Blackjack</h1>
        </div>

        <div className="bj-rules-chip">
          <FaShieldAlt />
          Dealer stands soft 17
        </div>
      </header>

      <div className="bj-money-bar">
        <div className="bj-money-stat">
          <span>Play Chips</span>
          <strong>{formatMoney(bankroll)}</strong>
        </div>

        <div className="bj-money-stat">
          <span>
            {phase === "player" ||
            phase === "dealer" ||
            phase === "insurance"
              ? "On Table"
              : "Next Wager"}
          </span>
          <strong>{formatMoney(displayWager)}</strong>
        </div>

        <div className="bj-bet-controls">
          <button
            type="button"
            onClick={() => changeWager(-BET_STEP)}
            disabled={
              (phase !== "idle" &&
                phase !== "complete") ||
              wager <= MIN_BET
            }
          >
            − {formatMoney(BET_STEP)}
          </button>

          <button
            type="button"
            onClick={() => changeWager(BET_STEP)}
            disabled={
              (phase !== "idle" &&
                phase !== "complete") ||
              wager + BET_STEP > bankroll
            }
          >
            + {formatMoney(BET_STEP)}
          </button>
        </div>

        <button
          type="button"
          className="bj-atm"
          onClick={refillBankroll}
          disabled={!atmAvailable || (currentUser?.id && Number(walletMeta?.refills_remaining || 0) <= 0)}
          title={
            atmAvailable
              ? currentUser?.id
                ? "Refresh play chips if a free refresh is available"
                : "Refresh local play chips"
              : `Chip refresh unlocks when your bankroll falls below ${formatMoney(MIN_BET)}`
          }
        >
          <FaUniversity />
          {atmAvailable
            ? currentUser?.id
              ? Number(walletMeta?.refills_remaining || 0) > 0
                ? `Refresh to ${formatMoney(STARTING_BANKROLL)}`
                : `Next refresh ${formatRefillCountdown(walletMeta?.next_refill_at) || "later"}`
              : `Refresh to ${formatMoney(STARTING_BANKROLL)}`
            : "Refresh Locked"}
        </button>
      </div>

      <div className="bj-layout">
        <main className="bj-table">
          <section className="bj-dealer-zone">
            <div className="bj-zone-heading">
              <div>
                <span>House</span>
                <h2>Dealer</h2>
              </div>

              {dealerHand.length > 0 && (
                <strong>
                  {phase === "player" ||
                  phase === "insurance"
                    ? `Showing ${visibleDealerTotal}`
                    : `Total ${visibleDealerTotal}`}
                </strong>
              )}
            </div>

            <div className="bj-cards">
              {dealerHand.map((card, index) => (
                <React.Fragment key={card.id}>
                  {renderCard(
                    card,
                    (phase === "player" ||
                      phase === "insurance") &&
                      index === 1
                  )}
                </React.Fragment>
              ))}
            </div>
          </section>

          <div
            className="bj-table-message"
            aria-live="polite"
          >
            {message}
          </div>

          <section className="bj-player-zone">
            <div className="bj-zone-heading">
              <div>
                <span>Player</span>
                <h2>
                  Your Hand
                  {playerHands.length > 1 ? "s" : ""}
                </h2>
              </div>

              {phase === "player" &&
                activeHand && (
                  <strong>
                    Hand {currentHand + 1} of{" "}
                    {playerHands.length}
                  </strong>
                )}
            </div>

            {!playerHands.length ? (
              <div className="bj-start-state">
                <div className="bj-start-state__icon">
                  ♠
                </div>

                <h3>Ready to deal?</h3>

                <p>
                  Your Blackjack play-chip balance is shared across solo and online play. Adjust your wager, then
                  play a full blackjack hand with
                  splits, doubles, surrender, and
                  insurance when the dealer shows an ace.
                </p>

                <button
                  onClick={startRound}
                  className="bj-primary"
                  disabled={bankroll < MIN_BET}
                >
                  <FaPlay />
                  Deal{" "}
                  {formatMoney(
                    Math.min(wager, bankroll)
                  )}
                </button>
              </div>
            ) : (
              <div className="bj-hands">
                {playerHands.map(
                  (hand, index) => {
                    const value =
                      handValue(hand.cards);

                    const active =
                      phase === "player" &&
                      index === currentHand;

                    return (
                      <article
                        key={index}
                        className={`bj-hand ${
                          active
                            ? "is-active"
                            : ""
                        }`}
                      >
                        <div className="bj-hand__top">
                          <strong>
                            Hand {index + 1}
                          </strong>

                          <div className="bj-hand__numbers">
                            <span>
                              {formatMoney(
                                hand.bet
                              )}
                            </span>
                            <b>
                              {value.total}
                              {value.soft
                                ? " soft"
                                : ""}
                            </b>
                          </div>
                        </div>

                        <div className="bj-cards bj-cards--player">
                          {hand.cards.map(
                            (card) => (
                              <React.Fragment
                                key={card.id}
                              >
                                {renderCard(card)}
                              </React.Fragment>
                            )
                          )}
                        </div>

                        {hand.result && (
                          <div
                            className={`bj-result ${
                              hand.result.includes(
                                "win"
                              ) ||
                              hand.result.includes(
                                "Blackjack"
                              )
                                ? "is-win"
                                : hand.result.includes(
                                    "Push"
                                  )
                                ? "is-push"
                                : "is-loss"
                            }`}
                          >
                            {hand.result}
                          </div>
                        )}

                        <div className="bj-hand__tags">
                          {hand.fromSplit && (
                            <span>
                              Split hand
                            </span>
                          )}

                          {hand.doubled && (
                            <span>Doubled</span>
                          )}

                          {hand.surrendered && (
                            <span>
                              Surrendered
                            </span>
                          )}
                        </div>
                      </article>
                    );
                  }
                )}
              </div>
            )}

            {phase === "insurance" && insuranceOffer && (
              <div className="bj-insurance-panel">
                <div className="bj-insurance-panel__copy">
                  <span className="bj-insurance-eyebrow">Insurance offered</span>
                  <h3>Dealer is showing an ace</h3>
                  <p>
                    You may wager up to half your original bet. Insurance pays 2:1
                    if the dealer has blackjack.
                  </p>
                </div>

                <div className="bj-insurance-actions">
                  <button
                    type="button"
                    onClick={() => handleInsuranceDecision(false)}
                    className="bj-insurance-button is-recommended"
                  >
                    <FaLightbulb />
                    No Insurance
                    <span className="bj-recommended-badge">Recommended</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInsuranceDecision(true)}
                    className="bj-insurance-button"
                    disabled={insuranceOffer.max <= 0}
                  >
                    Take Insurance
                    <strong>{formatMoney(insuranceOffer.max)}</strong>
                  </button>
                </div>

                <small>
                  Basic strategy generally declines insurance because it is a
                  separate side bet with unfavorable odds without card-counting information.
                </small>
              </div>
            )}

            {phase === "player" && (
              <div className="bj-strategy-controls">
                <button
                  type="button"
                  className="bj-strategy-toggle"
                  onClick={() => setShowRecommendations((prev) => !prev)}
                >
                  <FaLightbulb />
                  {showRecommendations ? "Hide Recommendations" : "Show Recommendations"}
                </button>
              </div>
            )}

            {phase === "player" && recommendation && (
              <div className="bj-strategy-tip">
                <FaLightbulb />
                <div>
                  <span>Basic Strategy</span>
                  <strong>Recommended: {recommendation.label}</strong>
                  <p>{recommendation.reason}</p>
                </div>
              </div>
            )}

            {phase === "player" && (
              <>
              <div className="bj-actions">
                <button
                  onClick={hit}
                  className={`${actionClass("hit")} bj-action--hit`}
                >
                  <FaBolt />
                  Hit
                  {recommendation?.action === "hit" && (
                    <span className="bj-action-recommended">Recommended</span>
                  )}
                </button>

                <button
                  onClick={stand}
                  className={actionClass("stand")}
                >
                  <FaHandPaper />
                  Stand
                  {recommendation?.action === "stand" && (
                    <span className="bj-action-recommended">Recommended</span>
                  )}
                </button>

                <button
                  onClick={doubleDown}
                  className={actionClass("double")}
                  disabled={!canDouble}
                  title={
                    canDouble
                      ? `Add ${formatMoney(
                          activeHand?.bet
                        )} and receive one final card`
                      : "Not enough chips or double is unavailable"
                  }
                >
                  <FaMoneyBillWave />
                  Double
                  {recommendation?.action === "double" && canDouble && (
                    <span className="bj-action-recommended">Recommended</span>
                  )}
                </button>

                <button
                  onClick={split}
                  className={actionClass("split")}
                  disabled={!canSplit}
                  title={
                    canSplit
                      ? `Add ${formatMoney(
                          activeHand?.bet
                        )} for the second hand`
                      : canSplitCards(activeHand?.cards || [])
                      ? `Split is legal, but you need ${formatMoney(activeHand?.bet || 0)} available to match the wager`
                      : "Split requires two equal-value cards"
                  }
                >
                  <FaLayerGroup />
                  Split
                  {recommendation?.action === "split" && canSplit && (
                    <span className="bj-action-recommended">Recommended</span>
                  )}
                </button>

                <button
                  onClick={surrender}
                  className={`${actionClass("surrender")} bj-action--danger`}
                  disabled={!canSurrender}
                >
                  Surrender
                  {recommendation?.action === "surrender" && canSurrender && (
                    <span className="bj-action-recommended">Recommended</span>
                  )}
                </button>
              </div>

              {activeHand && (
                <div className="bj-action-notes">
                  {canSplitCards(activeHand.cards) && !canSplit && splitFundingShortfall > 0 && (
                    <span>
                      Split is legal, but requires another {formatMoney(activeHand.bet)} wager.
                      You are short {formatMoney(splitFundingShortfall)}.
                    </span>
                  )}

                  {activeHand.cards.length === 2 && !canDouble && doubleFundingShortfall > 0 && (
                    <span>
                      Double requires another {formatMoney(activeHand.bet)} wager.
                      You are short {formatMoney(doubleFundingShortfall)}.
                    </span>
                  )}
                </div>
              )}
              </>
            )}

            {phase === "complete" && (
              <button
                onClick={startRound}
                className="bj-primary bj-play-again"
                disabled={bankroll < MIN_BET}
              >
                <FaRedoAlt />
                Deal Again
              </button>
            )}
          </section>
        </main>

        <aside className="bj-side">
          <section className="bj-panel bj-bankroll-panel">
            <span className="bj-panel__eyebrow">
              Bankroll
            </span>

            <h3 className="bj-bankroll-title">
              <FaCoins />
              {formatMoney(bankroll)}
            </h3>

            <p className="bj-bankroll-copy">
              Play chips only — no real money or cash
              value.
            </p>

            {currentUser?.id ? (
              <>
                <p className="bj-atm-note">
                  {Number(walletMeta?.refills_remaining ?? 2)} free refresh{Number(walletMeta?.refills_remaining ?? 2) === 1 ? "" : "es"} remaining in this 24-hour window.
                </p>
                {atmAvailable && Number(walletMeta?.refills_remaining || 0) <= 0 ? (
                  <p className="bj-atm-note">
                    Next free refresh in {formatRefillCountdown(walletMeta?.next_refill_at) || "under a minute"}.
                  </p>
                ) : null}
              </>
            ) : atmAvailable ? (
              <button
                className="bj-atm bj-atm--wide"
                onClick={refillBankroll}
              >
                <FaUniversity />
                Refresh to {formatMoney(STARTING_BANKROLL)}
              </button>
            ) : (
              <p className="bj-atm-note">
                Chip refresh becomes available when your bankroll falls below{" "}
                {formatMoney(MIN_BET)}.
              </p>
            )}
          </section>

          <section className="bj-panel">
            <span className="bj-panel__eyebrow">
              Session
            </span>

            <h3>Table Stats</h3>

            <div className="bj-stats">
              <div>
                <span>Wins</span>
                <strong>{stats.wins}</strong>
              </div>

              <div>
                <span>Losses</span>
                <strong>{stats.losses}</strong>
              </div>

              <div>
                <span>Pushes</span>
                <strong>{stats.pushes}</strong>
              </div>

              <div>
                <span>Blackjacks</span>
                <strong>{stats.blackjacks}</strong>
              </div>

              <div className="bj-stats__wide">
                <span>Hands Played</span>
                <strong>{stats.hands}</strong>
              </div>
            </div>
          </section>

          <section className="bj-panel">
            <span className="bj-panel__eyebrow">
              Table rules
            </span>

            <h3>Hobbli House</h3>

            <ul>
              <li>{SHOE_DECKS}-deck shoe</li>
              <li>Blackjack pays 3:2</li>
              <li>Insurance pays 2:1</li>
              <li>Dealer stands on soft 17</li>
              <li>Double on the first two cards</li>
              <li>
                Split up to {MAX_SPLIT_HANDS} hands
              </li>
              <li>
                Split hands are played one at a time
              </li>
              <li>
                A player may continue hitting on 21
              </li>
              <li>
                Late surrender before another card
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
